import { IHeaderGroupAngularComp } from '@ag-grid-community/angular';
import { IHeaderGroupParams } from '@ag-grid-community/core';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { ResponsiveService, ToggleButton } from '@frk/eds-components';
import { ProfileService, SiteConfigService } from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import {
  CalcTypeStd,
  ConfigurationId,
  CurrencyCode,
  DropdownOption,
  PerformanceType,
  PerformanceView,
  PerformanceViewOption,
} from '@types';
import { Logger } from '@utils/logger';
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
  FundListingPerformanceController,
  isFundListingCombinedController,
} from '../../controllers';

const logger = Logger.getLogger('PerformanceGroupHeaderComponent');

export interface PerformanceGroupHeaderParams
  extends FundListingPerformanceController,
    IHeaderGroupParams {}

@Component({
  selector: 'ft-performance-group-header',
  templateUrl: './performance-group-header.component.html',
  styleUrls: ['./performance-group-header.component.scss'],
})
export class PerformanceGroupHeaderComponent
  implements IHeaderGroupAngularComp, OnChanges, OnDestroy {
  // this (using ngOnchanges) allows the switch to be used as a normal component in card view
  @Input() controller: FundListingPerformanceController;
  public performanceAsOf$: Observable<string>;

  public performanceViewOptions: PerformanceViewOption[];
  private performanceViewOption: PerformanceViewOption;

  public performanceTypeToggle: ToggleButton<PerformanceType>[] = [];

  public periodEndToggle: ToggleButton<PerformanceView>[] = [];
  private periodEnd: PerformanceView;
  private unsubscribe$: Subject<void> = new Subject<void>();

  public performanceCurrencyOptions: DropdownOption[] = [];
  public selectedPerformanceCurrencyOption: DropdownOption;
  public isSiteIntl: boolean;
  public selectedPerfTypeHeaderKey$: Observable<string>;
  public placement: string;
  public isMobile$: Observable<boolean>;
  public isHandheld$: Observable<boolean>;
  public showGroupHeader = true;
  public showPerformanceCurrencyDropdown: boolean;

  constructor(
    private translateService: TranslateService,
    private responsiveService: ResponsiveService,
    private profileService: ProfileService,
    private siteConfig: SiteConfigService
  ) {
    this.isMobile$ = this.responsiveService.isMobile$();
    this.isHandheld$ = this.responsiveService.isHandheld$();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    logger.debug('ngOnChanges()', changes);
    if (changes.controller) {
      this.init();
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public agInit(params: PerformanceGroupHeaderParams): void {
    logger.debug('agInit()', params);
    this.controller = params;
    this.controller.setGridAPIs(params);
    this.init();
  }

  private init(): void {
    logger.debug('init()', this.controller);
    this.performanceAsOf$ = this.controller.getPerformanceAsOf$();
    logger.debug(
      'this.performanceViewOptions',
      this.controller.performanceViewOptions
    );

    // set initial PerformanceViewOptions
    this.performanceViewOptions = this.controller.performanceViewOptions || [];
    // set initial PerformanceView
    this.performanceViewOption = this.controller
      .getPerformanceViewOption$()
      .getValue();

    if (!this.performanceViewOption) {
      return;
    }

    // NGC-13646: SA - Performance currency calculation
    this.showPerformanceCurrencyDropdown = this.siteConfig.showPerformanceCurrency();

    // NGC-14354: SOUTH AFRICA - DEVICES - PRE - PPSS - Performance Currency drop down duplicate options
    // agInit called multiple times for displaying ppss page on handheld devices. Due to this
    // multiple dropdowns with similar values were created. Solving it here.
    if (this.showPerformanceCurrencyDropdown) {
      this.controller.performanceCurrencies?.forEach(
        (currency: CurrencyCode): void => {
          if (
            !this.performanceCurrencyOptions.find(
              (option: DropdownOption) => option.value === currency
            )
          ) {
            this.performanceCurrencyOptions.push({
              label:
                currency === ('SHARE_CLASS' as CurrencyCode)
                  ? this.translateService.instant(
                      `products.ppss-performance-currency-share-class-option`
                    )
                  : currency,
              value: currency,
            });
          }
        }
      );
      this.selectedPerformanceCurrencyOption = this.performanceCurrencyOptions.find(
        (currencyOption: DropdownOption): boolean =>
          currencyOption.value === this.controller.getPerformanceCurrency()
      );
    }

    this.isSiteIntl = this.siteConfig.isSiteInternational();

    // perf type header label (joined by kebab case)
    logger.debug(
      'getSelectedCalcTypes()',
      this.controller.getSelectedCalcTypes()
    );
    const selectedCalcType: CalcTypeStd = this.controller.getSelectedCalcType();
    this.selectedPerfTypeHeaderKey$ = this.controller.getCalcType$().pipe(
      map((calcTypeStd: CalcTypeStd): string => {
        let selectedPerfTypeHeaderKey = `${this.performanceViewOptions[0].headerLabelKey}-${calcTypeStd}`;

        // NGC-5376: Make label key different for SMA.
        if (this.controller.productType === ConfigurationId.SMA) {
          selectedPerfTypeHeaderKey += '-sma';
        }

        return selectedPerfTypeHeaderKey;
      })
    );

    // TODO: reuse Ag Grid helper service function?
    this.placement = `${
      this.controller.componentName
    }:${this.controller.productType.replace(
      /-|\s/g,
      ''
    )}TotalReturnsPerformance${upperFirst(camelCase(selectedCalcType))}`;

    if (this.performanceViewOptions?.length > 0) {
      this.performanceTypeToggle = this.performanceViewOptions.map(
        (
          performanceViewOption: PerformanceViewOption
        ): ToggleButton<PerformanceType> => ({
          active: performanceViewOption.isDefault,
          text: this.translateService.instant(performanceViewOption.labelKey),
          value: performanceViewOption.name,
        })
      );
    }

    logger.debug(
      'this.performanceViewOptions',
      this.performanceViewOptions,
      this.performanceTypeToggle
    );

    // TODO: ideally should use the async isLoggedIn$() version
    // hide this cmp for VIP funds if: user is on PPSS page AND (not logged in OR not identified)
    if (
      this.controller.productType === ConfigurationId.VIP &&
      !this.profileService.isLoggedIn() &&
      !isFundListingCombinedController(this.controller)
    ) {
      this.showGroupHeader = false;
    }

    // listen for periodEnd changes
    this.controller
      .getPeriodEnd$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((periodEnd: PerformanceView): void => {
        this.periodEnd = periodEnd;
        if (this.performanceViewOption.periodEnds) {
          this.periodEndToggle = this.performanceViewOption.periodEnds.map(
            this.mapPeriodEnds
          );
        }
      });
  }

  /**
   * Update the performance view as per type selected.
   * @param selectedToggleOption toggle option selected by user
   */
  public onPerformanceTypeUpdate(
    selectedToggleOption: ToggleButton<PerformanceType>
  ): void {
    this.controller.setPerformanceType(selectedToggleOption.value);
  }

  /**
   * Update the performance view as per type selected.
   * @param selectedToggleOption toggle option selected by user
   */
  public onPeriodEndUpdate(
    selectedToggleOption: ToggleButton<PerformanceView>
  ): void {
    this.controller.setPeriodEnd(selectedToggleOption.value);
  }

  /**
   * Update the performance currency as per option selected.
   * @param selectedDropdownOption option selected by user
   */
  public onPerformanceCurrencyUpdate(
    selectedDropdownOption: DropdownOption
  ): void {
    this.controller.setPerformanceCurrency(
      selectedDropdownOption.value as CurrencyCode
    );
  }

  private mapPeriodEnds = (
    periodEnd: PerformanceView
  ): ToggleButton<PerformanceView> => ({
    text: this.translateService.instant(`products.${periodEnd}`),
    active: periodEnd === this.periodEnd,
    value: periodEnd,
  });
}
